import React, { useMemo } from "react";
import { useTranslation } from 'react-i18next';
import '../css/Analyse.css';

/**
 * Analyse des évolutions entre les scans
 * @param {Array<number>} currentScan - valeurs actuelles (7)
 * @param {Array<number>} prevScan - valeurs du scan précédent (7)
 * @param {Array<number>} firstScan - valeurs du scan initial
 * @param {Array<Array<number>>} fullSequence - tous les scans filtrés (pour détecter la valeur stable max)
 * @param {Array<{labelLeft:string, labelRight:string}>} labels - les 7 dimensions
 */

const translationKeys = [
  { min: 'red.min', max: 'red.max' },
  { min: 'orange.min', max: 'orange.max' },
  { min: 'yellow.min', max: 'yellow.max' },
  { min: 'green.min', max: 'green.max' },
  { min: 'cyan.min', max: 'cyan.max' },
  { min: 'blue.min', max: 'blue.max' },
  { min: 'purple.min', max: 'purple.max' },
];

const COLORS = ['#C11D24', '#DA5E20', '#E0B614', '#29A648', '#029FB2', '#375A9C', '#6F3F89'];

const Analyse = ({ currentScan, prevScan, firstScan, fullSequence, labels }) => {
  const { t } = useTranslation();

  const biggestShift = useMemo(() => {
    if (!currentScan || !prevScan) return null;

    let maxDelta = 0;
    let dimensionIndex = null;

    currentScan.forEach((val, i) => {
      const delta = val - prevScan[i];
      if (Math.abs(delta) > Math.abs(maxDelta)) {
        maxDelta = delta;
        dimensionIndex = i;
      }
    });

    if (dimensionIndex === null || Math.abs(maxDelta) < 0.01) return null;

    return { index: dimensionIndex, delta: maxDelta };
  }, [currentScan, prevScan]);

  // 2. Tendance moyenne
  const averageTrend = useMemo(() => {
    if (!firstScan || !currentScan) return null;
    const avgFirst = firstScan.reduce((s, v) => s + v, 0) / firstScan.length;
    const avgCurrent = currentScan.reduce((s, v) => s + v, 0) / currentScan.length;
    return { diff: avgCurrent - avgFirst };
  }, [firstScan, currentScan]);

  // 3. Équilibre global gauche/droite
  const polarity = useMemo(() => {
    if (!currentScan) return null;
    const avg = currentScan.reduce((s, v) => s + v, 0) / currentScan.length;
    return avg; // >0 penche à droite (positif), <0 penche à gauche (négatif)
  }, [currentScan]);

  // 4. Valeur la plus élevée stable dans la séquence
  const stableMax = useMemo(() => {
    if (!fullSequence || fullSequence.length === 0) return null;

    // Somme par dimension
    const sums = Array(labels.length).fill(0);
    fullSequence.forEach(scan => {
      scan.forEach((val, i) => {
        sums[i] += val;
      });
    });

    const avgs = sums.map(s => s / fullSequence.length);
    const maxIndex = avgs.indexOf(Math.max(...avgs));

    return { index: maxIndex, value: avgs[maxIndex] };
  }, [fullSequence, labels]);

// ? `Progression vers ${biggestShift.dimension.labelRight} (+${Math.round(biggestShift.delta)})`
// : `Glissement vers ${biggestShift.dimension.labelLeft} (${Math.round(biggestShift.delta)})`}
  
return (
    <div className="analysis-container">
        {biggestShift && (
            <div className="analysis-item analysis-shift">
              <div className="analysis-text">
                <strong>{t('stats.pie.variation.value')}</strong>{" "}
                <div dangerouslySetInnerHTML={{
                __html: biggestShift.delta >= 0
                  ? t('stats.pie.variation.progression', {
                      label: `<span style="color:${COLORS[biggestShift.index]}"><b>${t(translationKeys[biggestShift.index].max)}</b></span>`,
                      delta: Math.round(biggestShift.delta),
                    })
                  : t('stats.pie.variation.shift', {
                      label: `<span style="color:${COLORS[biggestShift.index]}"><b>${t(translationKeys[biggestShift.index].min)}</b></span>`,
                      delta: Math.round(biggestShift.delta),
                    })
              }}/>
              </div>
            </div>
          )}

        {averageTrend && (
          <div className="analysis-item analysis-trend">
            <div className="analysis-text">
              <strong>{t('stats.pie.trend.value')}</strong>{" "}
              {averageTrend.diff >= 0
                ? t('stats.pie.trend.diff', { diff: Math.round(averageTrend.diff) })
                : t('stats.pie.trend.diffNegative', { diff: Math.round(averageTrend.diff) })}
            </div>
          </div>
        )}

        {polarity !== null && (
          <div className="analysis-item analysis-balance">
            <div className="analysis-text">
              <strong>{t('stats.pie.balance.value')}</strong>{" "}
              {polarity >= 0
                ? t('stats.pie.balance.positive')
                : t('stats.pie.balance.negative')}
            </div>
          </div>
        )}

        {stableMax && (
          <div className="analysis-item analysis-stable">
            <div className="analysis-text">
              <strong>{t('stats.pie.stable.value')}</strong>{" "}
              <div dangerouslySetInnerHTML={{
                __html: t('stats.pie.stable.axis', {
                  value: stableMax.value.toFixed(1),
                  label: `<span style="color:${COLORS[stableMax.index]}"><b>${t(translationKeys[stableMax.index].max)}</b></span>`
                })
              }}/>
            </div>
          </div>
        )}
      </div>
  );
};

export default Analyse;
